import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import OutlinedInput from '@mui/material/OutlinedInput';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { isEmpty } from 'src/common/helper';
import BtnGradient from 'src/components/common/button/BtnGradient';
import { useNotify } from 'src/context/ContextNotify';
import { postAPI } from 'src/utils/api';
import { getToken } from 'src/utils/auth';

const styleInput = {
  fontSize: '14px',
  my: '8px',
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: '#BDBDBD',
  },
  '& .MuiInputBase-input': { padding: '8px 14px' },
  '& input::placeholder': {
    fontSize: '14px',
    fontWeight: 500,
    color: '#CCD1D9',
    opacity: 1,
  },
  backgroundColor: 'primary.light',
  width: '100%',
};

const defaultError = {
  oldPassword: true,
  newPassword: true,
  confirmPassword: true,
};

const ChangPassword = () => {
  const notifyContext = useNotify();
  const navigate = useNavigate();
  const [data, setData] = useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [errors, setErrors] = useState(defaultError);
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleClickShowOldPassword = () => setShowOldPassword((show) => !show);
  const handleClickShowNewPassword = () => setShowNewPassword((show) => !show);
  const handleClickShowConfirmPassword = () =>
    setShowConfirmPassword((show) => !show);

  const handleMouseDownPassword = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
  };

  const handleInputChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = event.target;
    setData({
      ...data,
      [name]: value,
    });
  };

  const validateSave = () => {
    if (isEmpty(data.oldPassword)) {
      setErrors({ ...defaultError, oldPassword: false });
      notifyContext.setNotifyMessage({
        show: true,
        message: '古いパスワードを入力して下さい',
        type: 'error',
      });
      return false;
    }
    if (isEmpty(data.newPassword)) {
      setErrors({ ...defaultError, newPassword: false });
      notifyContext.setNotifyMessage({
        show: true,
        message: 'パスワードを入力して下さい',
        type: 'error',
      });
      return false;
    }
    if (isEmpty(data.confirmPassword)) {
      setErrors({ ...defaultError, confirmPassword: false });
      notifyContext.setNotifyMessage({
        show: true,
        message: 'パスワードを再入力して下さい',
        type: 'error',
      });
      return false;
    }
    if (
      !isEmpty(data.newPassword) &&
      !isEmpty(data.confirmPassword) &&
      data.newPassword !== data.confirmPassword
    ) {
      // setErrors({ ...defaultError, thedate: false });
      notifyContext.setNotifyMessage({
        show: true,
        message: 'パスワードが一致しませんでした',
        type: 'error',
      });
      return false;
    }
    return true;
  };

  const handleSave = () => {
    if (!validateSave()) {
      return;
    }
    try {
      const token = getToken();
      postAPI(`/auth/change-password?token=${token}`, {
        old_password: data.oldPassword,
        password: data.newPassword,
      }).then((res) => {
        if (res && res.code === 200) {
          window.localStorage.setItem('auth_data', '');
          window.localStorage.setItem('auth_token', '');
          navigate('/login');
        }
        if (res.code === 501 || res.code === 500) {
          notifyContext.setNotifyMessage({
            show: true,
            message: res.msg || 'Failed to post data',
            type: 'error',
          });
        }
      });
    } catch (error) {
      console.error('Failed to post data:', error);
      return;
    }
  };

  return (
    <Stack>
      <Stack
        width={'785px'}
        sx={{ backgroundColor: 'primary.light' }}
        borderRadius={'4px'}
        mb={'20px'}
      >
        <Grid container spacing={3}>
          <Grid item xs={4}>
            <Stack p={'14px 26px'} direction={'row'}>
              <Typography fontSize={'12px'} fontWeight={700}>
                現在のパスワード
              </Typography>
              <Typography color={'#ED5565'}>*</Typography>
            </Stack>
          </Grid>
          <Grid item xs>
            <Box p={'8px'}>
              <OutlinedInput
                error={!errors.oldPassword}
                placeholder={'入力してください'}
                sx={styleInput}
                inputProps={{
                  sx: {
                    backgroundColor: 'primary.light',
                  },
                }}
                autoComplete='one-time-code'
                name='oldPassword'
                value={data.oldPassword}
                onChange={(event) => handleInputChange(event)}
                type={showOldPassword ? 'text' : 'password'}
                endAdornment={
                  <InputAdornment position='end'>
                    <IconButton
                      aria-label='toggle password visibility'
                      onClick={handleClickShowOldPassword}
                      onMouseDown={handleMouseDownPassword}
                      edge='end'
                      sx={{ mr: '-6px' }}
                    >
                      {showOldPassword ? (
                        <Box
                          component={'img'}
                          src='/images/ic-eye.svg'
                          alt='icon eye'
                        />
                      ) : (
                        <Box
                          component={'img'}
                          src='/images/ic-eye-slash.svg'
                          alt='icon eye slash'
                        />
                      )}
                    </IconButton>
                  </InputAdornment>
                }
              />
            </Box>
          </Grid>
        </Grid>
        <Divider sx={{ borderColor: '#F3F3F3' }} />
        <Grid container spacing={3}>
          <Grid item xs={4}>
            <Stack p={'14px 26px'} direction={'row'}>
              <Typography fontSize={'12px'} fontWeight={700}>
                新しいパスワード
              </Typography>
              <Typography color={'#ED5565'}>*</Typography>
            </Stack>
          </Grid>
          <Grid item xs>
            <Box p={'8px'}>
              <OutlinedInput
                error={!errors.newPassword}
                placeholder={'入力してください'}
                sx={styleInput}
                inputProps={{
                  sx: {
                    backgroundColor: 'primary.light',
                  },
                }}
                autoComplete='one-time-code'
                name='newPassword'
                value={data.newPassword}
                onChange={(event) => handleInputChange(event)}
                type={showNewPassword ? 'text' : 'password'}
                endAdornment={
                  <InputAdornment position='end'>
                    <IconButton
                      aria-label='toggle password visibility'
                      onClick={handleClickShowNewPassword}
                      onMouseDown={handleMouseDownPassword}
                      edge='end'
                      sx={{ mr: '-6px' }}
                    >
                      {showNewPassword ? (
                        <Box
                          component={'img'}
                          src='/images/ic-eye.svg'
                          alt='icon eye'
                        />
                      ) : (
                        <Box
                          component={'img'}
                          src='/images/ic-eye-slash.svg'
                          alt='icon eye slash'
                        />
                      )}
                    </IconButton>
                  </InputAdornment>
                }
              />
            </Box>
          </Grid>
        </Grid>
        <Divider sx={{ borderColor: '#F3F3F3' }} />
        <Grid container spacing={3}>
          <Grid item xs={4}>
            <Stack p={'14px 26px'} direction={'row'}>
              <Typography fontSize={'12px'} fontWeight={700}>
                新しいパスワード（確認）
              </Typography>
              <Typography color={'#ED5565'}>*</Typography>
            </Stack>
          </Grid>
          <Grid item xs>
            <Box p={'8px'}>
              <OutlinedInput
                error={!errors.confirmPassword}
                placeholder={'入力してください'}
                sx={styleInput}
                inputProps={{
                  sx: {
                    backgroundColor: 'primary.light',
                  },
                }}
                autoComplete='one-time-code'
                name='confirmPassword'
                value={data.confirmPassword}
                onChange={(event) => handleInputChange(event)}
                type={showConfirmPassword ? 'text' : 'password'}
                endAdornment={
                  <InputAdornment position='end'>
                    <IconButton
                      aria-label='toggle password visibility'
                      onClick={handleClickShowConfirmPassword}
                      onMouseDown={handleMouseDownPassword}
                      edge='end'
                      sx={{ mr: '-6px' }}
                    >
                      {showConfirmPassword ? (
                        <Box
                          component={'img'}
                          src='/images/ic-eye.svg'
                          alt='icon eye'
                        />
                      ) : (
                        <Box
                          component={'img'}
                          src='/images/ic-eye-slash.svg'
                          alt='icon eye slash'
                        />
                      )}
                    </IconButton>
                  </InputAdornment>
                }
              />
            </Box>
          </Grid>
        </Grid>
        <Divider sx={{ borderColor: '#F3F3F3' }} />
      </Stack>
      <BtnGradient text='更新' handleButtonClick={handleSave} />
    </Stack>
  );
};

export default ChangPassword;
